/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useMemo } from 'react';

import objectPath from 'object-path';
import ReactSelect, { OptionsOrGroups, Props, PropsValue } from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import API from '../utils/api';

// One step above `.overlay` (z-50) in src/index.css.
const MENU_PORTAL_Z_INDEX = 60;

export const SelectStyle: any = {
  control: (css: any) => ({
    ...css,
    backgroundColor: 'white',
    borderRadius: '10px',
    border: '1px solid #C3CBDF',
    boxSizing: 'border-box',
    boxShadow: '0px',
  }),
  option: (css: any, { isFocused, isSelected }: any) => {
    let optionColor = isFocused ? '#E1E5EF' : '';
    if (isSelected) {
      optionColor = '#F0F2F7';
    }
    return {
      ...css,
      backgroundColor: optionColor,
      color: '#2B3230',
    };
  },
  menu: (css: any) => ({
    ...css,
    borderRadius: '5px',
  }),
  // react-select portals the menu with `zIndex: 1`, which paints it under the
  // `.overlay` (z-50) of any modal it is opened from — the dropdown loads its
  // options and renders them invisibly. Only applies when `menuPortalTarget`
  // is set, so it is inert for the selects that render in place.
  menuPortal: (css: any) => ({
    ...css,
    zIndex: MENU_PORTAL_Z_INDEX,
  }),
};

const normalizeData = <T,>(
  options: T[],
  fieldId: string,
  fieldLabel: string
): Array<T & { value: unknown; label: unknown }> =>
  options?.map((opt) => ({
    ...opt,
    value: opt[fieldId as keyof T],
    label: opt[fieldLabel as keyof T],
  }));

interface SelectProps<T> extends Props {
  fieldId?: string;
  fieldLabel?: string;
  options?: T[];
  value?: unknown;
}

interface RemoteSelectProps<T> extends AsyncProps<T, false, any> {
  fieldId?: string;
  fieldLabel?: string;
  value?: PropsValue<T> | undefined;
  url: string;
  dataPath?: string;
}

export const Select = <T,>({
  fieldId = 'value',
  fieldLabel = 'label',
  options = [],
  value = null,
  ...props
}: SelectProps<T>): JSX.Element => {
  const items: Array<T & { value: unknown; label: unknown }> = useMemo(() => {
    return normalizeData(options, fieldId, fieldLabel);
  }, [options, fieldId, fieldLabel]);

  const v = useMemo(() => {
    return items.find((i) => i.value === value);
  }, [items, value]);

  return <ReactSelect styles={SelectStyle} {...props} options={items} value={v} />;
};

export const RemoteSelect = <T,>({
  fieldId = 'value',
  fieldLabel = 'label',
  url,
  dataPath = '',
  ...props
}: RemoteSelectProps<T>): JSX.Element => {
  const loadOptions:
    | ((inputValue: string, callback: (options: OptionsOrGroups<T, any>) => void) => void)
    | ((inputValue: string) => Promise<OptionsOrGroups<T, any>>) = async (inputValue: string) => {
    const response = await API.get(`${url}${url.includes('?') ? '&' : '?'}search=${inputValue}`);
    const data: any[] = objectPath.get(response.data, dataPath);

    const options = data.map((item: any) => ({
      value: item[fieldId],
      label: item[fieldLabel],
    }));

    return options;
  };

  return <AsyncSelect styles={SelectStyle} {...props} loadOptions={loadOptions} defaultOptions />;
};
